import { useCallback, useContext, useEffect, useMemo, useState, useRef } from "react";
import { DLabEquipmentList as CommonAgGrid } from "@digitallab/grid-common-frontend-components";
import { OpenSearchConfig } from "../../components/shared/OpenSearchConfig";
import { CloudSearchConfig } from "../../components/shared/CloudSearchConfig";
import {
  DEFAULT_COLUMNS,
  DEFAULT_FILTER,
  DEFAULT_PAGE_SIZE,
  allTableColumn,
  entryType,
  listOfFieldsIgnoredForSearch,
  listOfNumberFilter,
  INVALID_BARCODE_MESSAGE,
  CAMERA_TIMEOUT_MESSAGE,
  DURATION,
  agGridAutoGroupfieldName,
  equipmentStatus,
  DEFAULT_MYLAB_FILTER,
} from "../../constants";
import { SNACKBAR_DURATION } from "../../components/EquipmentList/Constants/constants";
import { filter, find, keyBy, sortBy, uniq } from "lodash";
import {
  convertCloudToGraphQLObj,
  convertStrToObjColumns,
  convertToCameCase,
  convertToSnakeCase,
} from "../../utils/helpers/text";
import {
  buildDisplayDefinitionFromColumns,
  getColumnObj,
  onCreateSelectColumn,
  onCreatefilter,
  onDeleteSaveConfigurations,
  onUpdateFilter,
  onUpdateSelectColumn,
  rehydrateStoredColumn,
  saveLastPageSize,
} from "./EquipmentHelper";
import { TOOL_PANELS, filterKeyMapping } from "../../components/DLabGrid/dLabGridConstant";
import DATA_MODEL_TABLE from "../../utils/constants/dataModelTable";
import { useSelector } from "react-redux";
import { withApollo } from "react-apollo";
import {
  BOOKING_GET_LOGGED_USER_DETAILS,
  CREATE_USER_DISPLAY_COLUMN,
  CREATE_USER_FILTER,
  DELETE_USER_DISPLAY_COLUMN,
  DELETE_USER_FILTER,
  LP_GET_LOGGED_USER_DETAILS_USER_FILTERS,
  UPDATE_DIGITAL_LAB_BOOKING_USER_PROFILE_PERSONAL_FIELDS,
  UPDATE_USER_DISPLAY_COLUMN,
  UPDATE_USER_FILTER,
  UPDATE_DIGITAL_LAB_LANDING_PAGE_USER_FILTER,
  CREATE_DIGITAL_LAB_LANDING_PAGE_USER_FILTER,
} from "../../gql/bookingapi";
import { OwcIcon, OwcTypography, OwcBadge } from "@one/react";
import { useParams, useHistory } from "react-router-dom";
import { CustomFilterContext } from "../booking-instruments/booking-instruments-custom-filter/context";
import { TogglerContext } from "../../contexts";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";
import { ConfirmDialog } from "../../components/shared";
import { BookingContext, BookingPopUpContext } from "../booking/context";
import CidmGroupDialog from "./cidmGroupDialog";
import {
  createDefaultCsvExportParams,
  createDefaultExcelExportParams,
  createClipboardCellProcessor,
} from "../../utils/helpers/exportValueMapper";
import EquipmentAction from "./EquipmentAction";
import { EquipmentsContext } from "../equipment-context/context";
import ScanCodeButton from "../book-equipment/booking-list/ScanCodeButton";
import {
  getFieldValue,
  handleBookingFields,
  normalizeBooleanFilters,
  OPENSEARCH_CONFIGURATION,
} from "../booking-instruments/helpers";
import { accessControlStatus, getBookingAccessControlState } from "./accessControlStatus";
import {
  ACCESS_CONTROL_FETCH_SIZE,
  ensureBookEquipmentBaseConstraints,
  getRequestedAccessControlStatuses,
  stripAccessControlFilterClauses,
  stripDeletedStatusClauses,
} from "./accessControlFilterUtils";

const baseAttributes = {
  rowModelType: "serverSide",
  rowQuickSearch: false,
  treeData: true,
  suppressPaginationPanel: true,
  pagination: true,
  animateRows: true,
  rowSelection: "multiple",
  rowMultiSelectWithClick: true,
  rowExport: false,
  masterDetail: true,
  isGroupOpenByDefault: false,
  defaultToolPanel: "filters",
  hiddenByDefault: true,
  serverSideInfiniteScroll: false,
  serverSideSortOnServer: true,
  suppressRowClickSelection: true,
  allowContextMenuWithControlKey: true,
  getContextMenuItems: (params) => {
    return [
      "copy",
      "copyWithHeaders",
      "copyWithGroupHeaders",
      "separator",
      {
        name: "Export",
        subMenu: [
          {
            name: "Export Selected Rows",
            subMenu: [
              {
                name: "CSV Export",
                action: () => {
                  const csvParams = {
                    ...params.api.gridOptionsService.get("defaultCsvExportParams"),
                  };
                  params.api.exportDataAsCsv(csvParams);
                },
              },
              {
                name: "Excel Export",
                action: () => {
                  const excelParams = {
                    ...params.api.gridOptionsService.get("defaultExcelExportParams"),
                  };
                  params.api.exportDataAsExcel(excelParams);
                },
              },
            ],
          },
          "separator",
          {
            name: "Export All (Filtered)",
            subMenu: [
              {
                name: "CSV Export",
                action: () => {
                  const csvParams = {
                    ...params.api.gridOptionsService.get("defaultCsvExportParams"),
                    onlySelected: false,
                  };
                  params.api.exportDataAsCsv(csvParams);
                },
              },
              {
                name: "Excel Export",
                action: () => {
                  const excelParams = {
                    ...params.api.gridOptionsService.get("defaultExcelExportParams"),
                    onlySelected: false,
                  };
                  params.api.exportDataAsExcel(excelParams);
                },
              },
            ],
          },
        ],
      },
    ];
  },
};

const normalizeConfigObject = (value) => {
  if (!value) {
    return null;
  }
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
};

const isSameConfig = (a, b) => {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

// Helper: determine whether an equipment status keeps the row selectable.
// Equipment that already exists but is temporarily in PENDING status (e.g. due to an
// ongoing edit) must remain selectable, so ACTIVE and PENDING are treated the same here.
// Exception: 4-eyes-principle (4EP) equipment must NOT be selectable while PENDING.
const FOUR_EP_ID_PREFIX = "4epf_";
const isNew4epEquipment = (id) => id.startsWith(FOUR_EP_ID_PREFIX);

const matchesEquipmentStatus = (normalizedStatus, statusDef) =>
  normalizedStatus === statusDef?.key || normalizedStatus === statusDef?.value?.toUpperCase();

const isNew4epPendingRow = (row) => {
  const id = (getFieldValue(row, "id") ?? "").toString();
  const status = (getFieldValue(row, "status") ?? "").toString().toUpperCase();
  return isNew4epEquipment(id) && matchesEquipmentStatus(status, equipmentStatus?.pending);
};

const getSelectableStatus = (status, id) => {
  const normalizedStatus = (status ?? "").toString().toUpperCase();
  const isActive = matchesEquipmentStatus(normalizedStatus, equipmentStatus?.active);
  const isPending = matchesEquipmentStatus(normalizedStatus, equipmentStatus?.pending);
  const isSelectableStatus = isActive || (isPending && !isNew4epEquipment(id));
  return { isActive, isPending, isSelectableStatus };
};

export const buildAgGridFilterObj = (currentState) => {
  const obj = {};
  const handledSnakeKeys = new Set();
  for (const key in DEFAULT_FILTER.filterDefinition) {
    const snakeKey = convertToSnakeCase(filterKeyMapping[key] || key);
    handledSnakeKeys.add(snakeKey);
    if (currentState[snakeKey] === undefined) {
      obj[key] = DEFAULT_FILTER.filterDefinition[key];
    } else if (currentState[snakeKey]?.filterType === "set") {
      let values = currentState[snakeKey]?.values;
      if (
        filterKeyMapping[key] === DATA_MODEL_TABLE.equipmentModel?.key ||
        key === DATA_MODEL_TABLE.equipmentModel?.key
      ) {
        if (Object.keys(currentState)?.includes(agGridAutoGroupfieldName)) {
          values = uniq([...values, ...currentState[agGridAutoGroupfieldName]?.values]);
        }
      }
      obj[key] = values;
    } else {
      obj[key] = currentState[snakeKey];
    }
  }
  for (const stateKey in currentState) {
    if (!handledSnakeKeys.has(stateKey) && stateKey !== agGridAutoGroupfieldName) {
      obj[stateKey] = currentState[stateKey]?.filterType === "set"
        ? currentState[stateKey]?.values
        : currentState[stateKey];
    }
  }
  return obj;
};

const isEquipmentBookable = (data) => {
  if (!data?.is_bookable) {
    return false;
  }
  const value = getFieldValue(data, "is_bookable");
  return value === "true" || value === true;
};

const canSelectEquipmentRow = ({ data, nodeLevel, storeUser }) => {
  const id = getFieldValue(data, "id");
  if (!id || id === "dummyRow") {
    return false;
  }

  const accessControlState = getBookingAccessControlState(data, storeUser);
  // BookingAdmins keep the ability to select booking-controlled instruments they
  // don't personally have access to (parity with the former cell-renderer override).
  // The structural gates below (top-level, bookable, status, cluster) still apply.
  const isBookingAdmin = storeUser.roles?.some((role) => role === "BookingAdmin");
  const hasAllowedAccess = isBookingAdmin || accessControlState?.status === accessControlStatus.allowed;
  const isTopLevel = nodeLevel === 0;
  const isNotInCluster = getFieldValue(data, "cluster_id", null) === null;
  const isBookable = isEquipmentBookable(data);
  const { isSelectableStatus } = getSelectableStatus(getFieldValue(data, "status"), id);

  return [hasAllowedAccess, isTopLevel, isNotInCluster, isBookable, isSelectableStatus].every(Boolean);
};

const extractPositiveClusterId = (query) => {
  if (typeof query !== "string" || !query.trim()) {
    return null;
  }

  const isConcreteClusterId = (value) => {
    const normalizedValue = String(value || "").trim();
    if (!normalizedValue) {
      return false;
    }

    // Ignore wildcard and pattern values; expansion rewrite requires a concrete parent id.
    if (normalizedValue.includes("*") || normalizedValue.includes("?")) {
      return false;
    }
    if (/^(AND|OR|NOT)$/i.test(normalizedValue)) {
      return false;
    }

    return true;
  };

  const matcher = /(^|[\s(]|\bAND\b|\bOR\b)(?<isNegated>-?)cluster_id\s*:\s*"?(?<clusterId>[^"\s)]+)"?/gi;
  let match;
  while ((match = matcher.exec(query)) !== null) {
    const { isNegated = "", clusterId = "" } = match.groups || {};
    if (isNegated !== "-" && isConcreteClusterId(clusterId)) {
      return clusterId || null;
    }
  }

  return null;
};

const buildClusterExpansionSearchValue = (clusterId) => {
  if (!clusterId) {
    return "";
  }

  return `(status:"${equipmentStatus.active.value}" OR status:"${equipmentStatus.pending.value}") AND cluster_id:"${clusterId}"`;
};

const normalizeBooleanOperators = (query) => {
  if (typeof query !== "string") {
    return query;
  }

  const tokens = query.trim().match(/(?:[^\s"]*"[^"]*"|\S+)/g) || [];
  const normalizedTokens = [];

  tokens.forEach((token) => {
    const upperToken = token.toUpperCase();
    const isBinaryOperator = upperToken === "AND" || upperToken === "OR";

    if (!isBinaryOperator) {
      normalizedTokens.push(token);
      return;
    }

    if (!normalizedTokens.length) {
      return;
    }

    const previousToken = normalizedTokens[normalizedTokens.length - 1];
    const previousUpperToken = previousToken.toUpperCase();
    const previousIsOperator = previousUpperToken === "AND" || previousUpperToken === "OR";

    if (previousIsOperator) {
      normalizedTokens[normalizedTokens.length - 1] = upperToken;
      return;
    }

    normalizedTokens.push(upperToken);
  });

  while (normalizedTokens.length) {
    const tailToken = normalizedTokens[normalizedTokens.length - 1];
    const tailUpperToken = tailToken.toUpperCase();
    if (tailUpperToken !== "AND" && tailUpperToken !== "OR") {
      break;
    }
    normalizedTokens.pop();
  }

  return normalizedTokens.join(" ");
};

const stripClusterIdFromFilterQuery = (query) => {
  if (typeof query !== "string") {
    return query;
  }

  const withoutClusterId = query.replace(/\(?\s*-?cluster_id\s*:\s*"?[^"\s)]+"?\s*\)?/gi, " ");
  const withoutEmptyParens = withoutClusterId.replace(/\(\s*\)/g, " ");

  return normalizeBooleanOperators(withoutEmptyParens);
};

const normalizeRequestForClusterExpansion = (option = {}) => {
  const clusterId = extractPositiveClusterId(option.searchValue) || extractPositiveClusterId(option.filterQuery);

  if (!clusterId) {
    return option;
  }

  return {
    ...option,
    // Child-node loads should be scoped by parent cluster id and not by global text term,
    // otherwise intermediate sub-clusters are filtered out and the hierarchy is broken.
    searchValue: buildClusterExpansionSearchValue(clusterId),
    filterQuery: stripClusterIdFromFilterQuery(option.filterQuery),
  };
};

// Helper: return updated equipment list, skipping duplicates
const buildUpdatedEquipmentList = (prevState, data, inventoryId) => {
  if (prevState.some((eq) => eq.id === inventoryId)) {
    return prevState;
  }
  const newItem = convertCloudToGraphQLObj(data);
  return [...new Set([...prevState, newItem])];
};

const EquipmentMainPage = ({
  masterDataFilters,
  myLabFilter,
  lastFavFilter,
  setLastFavFilter,
  userFilters,
  setUserFilters,
  userDisplayColumn,
  setUserDisplayColumn,
  lastDisplayColumns,
  setLastDisplayColumns,
  updateLastDisplayColumnDefinition,
  lastPageSize,
  setLastPageSize,
  client,
  setEquipmentDetailList,
  isStepEnable,
  setNewEquipmentIds,
  limitExceedStatus,
  setLimitExceedStatus,
  landingPageAppSyncClient,
  reloadFilterData,
  paginationActions,
}) => {
  const equipmentListRef = useRef(null);
  const masterDataFilterGroups = useRef();
  masterDataFilterGroups.current = masterDataFilters;
  const [dataModel, setDataModel] = useState({});
  const [defaultShowColumns, setDefaultShowColumns] = useState({
    ...DEFAULT_COLUMNS(),
  });
  const { pendingReservations } = useSelector((state) => state.reservations);
  const { setSelectedList, selectedList } = useContext(CustomFilterContext);
  const { isEditMode, isOngoing } = useContext(BookingContext);
  const { dispatchAction: bookingPopUpDispatchAction } = useContext(BookingPopUpContext);
  const [onGoingEventArray] = useState(() => {
    let tempArray = [];
    if (isEditMode && isOngoing) {
      pendingReservations.forEach((x) => {
        if (x.isOngoingEventFlag) {
          tempArray.push(x.inventoryId);
        }
      });
    }
    return uniq(tempArray);
  });
  const [cidmDialogOpen, setCidmDialogOpen] = useState(false);
  const [cidmGroupData, setCidmGroupData] = useState({
    name: "",
    url: "",
    info: "",
  });
  const [additionalFields, setAdditionalFields] = useState([]);

  const [allAvailableFilters, setAllAvailableFilters] = useState([]);
  const [myLabCount, setMyLabCount] = useState([]);
  const storeUser = useSelector((store) => store.user);
  const localUserFilters = useRef(userFilters);
  const autoGroupColField = "equipment_model";
  const { listMessage, setListMessage } = useContext(TogglerContext);
  const navigation = useHistory();
  const { InventoryId } = useParams();
  const hasPreselectedFromRoute = useRef(false);
  const hasMyLabFilter = useRef(false);

  const attributes = useMemo(
    () => ({
      ...baseAttributes,
      defaultCsvExportParams: createDefaultCsvExportParams({}, {}, storeUser),
      defaultExcelExportParams: createDefaultExcelExportParams({}, {}, storeUser),
      processCellForClipboard: createClipboardCellProcessor({}, storeUser),
    }),
    [storeUser],
  );

  useEffect(() => {
    if (InventoryId && !hasPreselectedFromRoute.current) {
      setSelectedList((prevState) => onlyUnique([...prevState, InventoryId]));
      hasPreselectedFromRoute.current = true;
    }
  }, [InventoryId, setSelectedList]);

  const {
    myLabFiltersList,
    setMyLabEquipment,
    fetchingMyLabEquipment,
    setMyLabFiltersList,
    setAppliedFilter,
    myLabCountInitial,
    userID,
  } = useContext(EquipmentsContext);
  const openCidmDialog = useCallback(
    (item) => {
      setCidmGroupData({
        name: item.name ?? "",
        url: item.url ?? "",
        info: item.info ?? "",
      });
      setCidmDialogOpen(true);
    },
    [setCidmGroupData, setCidmDialogOpen],
  );

  useEffect(() => {
    const fetchUserFilters = async () => {
      const isMyLab = window.location.pathname?.includes("my-lab");
      if (!isMyLab && storeUser) {
        const cachedUserFilters = storeUser.userFilters?.items;
        const cachedSharedFilters = storeUser.sharedFilters;

        if (Array.isArray(cachedUserFilters) || Array.isArray(cachedSharedFilters)) {
          setAllAvailableFilters([...(cachedUserFilters ?? []), ...(cachedSharedFilters ?? [])]);
          return;
        }
      }
      try {
        const response = await client.query({
          query: isMyLab ? LP_GET_LOGGED_USER_DETAILS_USER_FILTERS : BOOKING_GET_LOGGED_USER_DETAILS,
          variables: { id: storeUser?.id },
          fetchPolicy: "no-cache",
        });
        const userDetails = response?.data?.getLoggedInUserDetails?.items[0];
        setAllAvailableFilters([...(userDetails?.userFilters?.items || []), ...(userDetails?.sharedFilters || [])]);
      } catch (e) {
        console.error("Failed to fetch logged-in user details:", e);
      }
    };

    fetchUserFilters();
  }, [client, storeUser]);

  useEffect(() => {
    const handleOpenCidmDialogEvent = (event) => {
      openCidmDialog(event.detail);
    };

    document.addEventListener("openCidmDialog", handleOpenCidmDialogEvent);

    return () => {
      document.removeEventListener("openCidmDialog", handleOpenCidmDialogEvent);
    };
  }, [openCidmDialog]);

  useEffect(() => {
    localUserFilters.current = userFilters;
  }, [userFilters]);

  useEffect(() => {
    if (myLabFiltersList && myLabFiltersList.length > 0) {
      const exists = myLabFiltersList.some((x) => x.filterName === DEFAULT_MYLAB_FILTER.filterName);
      if (exists) {
        hasMyLabFilter.current = true;
      }
    }
  }, [myLabFiltersList]);

  useEffect(() => {
    const INTERVAL = 1000; // ms
    const stop = (e) => {
      const header = e.target.closest(".ag-header-cell-comp-wrapper");
      if (header && /access control/i.test(header.textContent)) {
        e.stopPropagation();
        e.preventDefault();
      }
    };
    const lockHeaders = () => {
      document.querySelectorAll(".ag-header-cell-comp-wrapper").forEach((wrap) => {
        const label = wrap.querySelector(".ag-header-cell-text");
        if (label && /access control/i.test(label.textContent) && !wrap.dataset.acLocked) {
          wrap.style.pointerEvents = "none";
          wrap.addEventListener("click", stop, true);
          wrap.dataset.acLocked = "true";
        }
      });
    };

    lockHeaders();
    const id = setInterval(lockHeaders, INTERVAL);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const unorderedHidenColumn = [];
    const orderedShowColumns = sortBy(
      convertStrToObjColumns(lastDisplayColumns)?.displayDefinition?.showColumns,
      ["order"],
      ["asc"],
    );
    allTableColumn()?.forEach((value, index) => {
      const obj = find(orderedShowColumns, {
        key: value?.key,
      });
      if (!obj) {
        unorderedHidenColumn.push(value);
      }
    });
    // A stored entry is a layout record, not a column definition — see rehydrateStoredColumn.
    const schemaColumns = keyBy(allTableColumn(), "key");

    const redefinedColumns = [...orderedShowColumns, ...unorderedHidenColumn]?.map((value) => {
      return getColumnObj(
        rehydrateStoredColumn(schemaColumns, value),
        masterDataFilters,
        convertToCameCase(autoGroupColField),
      );
    });
    const defaultShowCols = filter(allTableColumn(), { show: true });

    setDefaultShowColumns(() => {
      return defaultShowCols?.map((value) => {
        return getColumnObj(value, masterDataFilters, convertToCameCase(autoGroupColField));
      });
    });
    const aditionColumns = [
      {
        field: "actions",
        headerName: "",
        maxWidth: 50,
        filter: false,
        pinned: "right",
        lockVisible: true,
        suppressColumnsToolPanel: true,
        suppressMenu: true,
        lockPosition: "right",
        cellClass: "action-render",
        cellRenderer: actionRender,
      },
    ];
    setAdditionalFields(() => [...aditionColumns]);
    setDataModel([...redefinedColumns]);
    // eslint-disable-next-line
  }, [masterDataFilters, lastDisplayColumns]);

  const requestServer = async (cloudSearchConfigOption) => {
    const normalizedRequestOption = normalizeRequestForClusterExpansion(cloudSearchConfigOption);
    normalizedRequestOption.searchValue = stripDeletedStatusClauses(normalizedRequestOption.searchValue);
    normalizedRequestOption.filterQuery = stripDeletedStatusClauses(normalizedRequestOption.filterQuery);
    const requestedStart = Number(cloudSearchConfigOption?.start ?? 0);
    const requestedSize = Number(cloudSearchConfigOption?.size ?? lastPageSize ?? DEFAULT_PAGE_SIZE);
    const requestedStatuses = getRequestedAccessControlStatuses(
      cloudSearchConfigOption?.searchValue,
      cloudSearchConfigOption?.filterQuery,
    );
    const hasAccessControlFilter = requestedStatuses.size > 0;

    const runSearch = (payload) =>
      OPENSEARCH_CONFIGURATION ? OpenSearchConfig(handleBookingFields(payload)) : CloudSearchConfig(payload);

    if (!hasAccessControlFilter) {
      const result = await runSearch(normalizedRequestOption);
      if (Array.isArray(result?.data)) {
        const filtered = result.data.filter((row) => !isNew4epPendingRow(row));
        return { ...result, data: filtered, count: result.count - (result.data.length - filtered.length) };
      }
      return result;
    }

    // Strip only Access Control clauses and force baseline list constraints.
    // Then fetch a full result set for globally correct FE filtering and pagination.
    const strippedSearchValue = normalizeBooleanFilters(
      stripAccessControlFilterClauses(normalizedRequestOption.searchValue),
    );
    const strippedFilterQuery = normalizeBooleanFilters(
      stripAccessControlFilterClauses(normalizedRequestOption.filterQuery),
    );

    const strippedOption = {
      ...normalizedRequestOption,
      start: 0,
      size: ACCESS_CONTROL_FETCH_SIZE,
      searchValue: ensureBookEquipmentBaseConstraints(strippedSearchValue),
      filterQuery: strippedFilterQuery,
    };

    const backendResult = await runSearch(strippedOption);
    const backendRows = Array.isArray(backendResult?.data) ? backendResult.data : [];
    const filteredRows = backendRows.filter((row) => {
      if (isNew4epPendingRow(row)) {
        return false;
      }
      const status = getBookingAccessControlState(row, storeUser).status;
      return requestedStatuses.has(status);
    });
    const pagedRows = filteredRows.slice(requestedStart, requestedStart + requestedSize);

    return {
      ...backendResult,
      data: pagedRows,
      count: filteredRows.length,
    };
  };
  const entryTypeCheck = (dataItem) => {
    if (OPENSEARCH_CONFIGURATION) {
      return getFieldValue(dataItem, "entry_type") === entryType?.cluster;
    } else {
      return dataItem?.entry_type?.includes(entryType?.cluster);
    }
  };
  const isServerSideGroup = useCallback((dataItem) => {
    // indicate if node is a group

    if (dataItem?.entry_type && entryTypeCheck(dataItem)) {
      return dataItem?.id;
    }

    return "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const getServerSideGroupKey = useCallback((dataItem) => {
    // specify which group key to use

    if (dataItem?.entry_type && entryTypeCheck(dataItem)) {
      return dataItem?.id;
    }

    return "";
  }, []);

  const checkUserBookingAccess = useCallback((data, storeUser) => {
    const { status } = getBookingAccessControlState(data, storeUser);
    return status === accessControlStatus.allowed;
  }, []);

  const onFirstDataRendered = useCallback(
    (params) => {
      if (!InventoryId || !selectedList.includes(InventoryId)) {
        return;
      }

      params.api.forEachNode((node) => {
        if (getFieldValue(node.data, "id") !== InventoryId) {
          return;
        }

        const data = node.data;
        if (!canSelectEquipmentRow({ data, nodeLevel: node?.level, storeUser })) {
          return;
        }

        node.setSelected(true);
        setEquipmentDetailList((prevState) => buildUpdatedEquipmentList(prevState, data, InventoryId));
      });
    },
    [InventoryId, selectedList, storeUser, setEquipmentDetailList],
  );

  const autoGroupColumnDef = useMemo(() => {
    return {
      headerName: "Model",
      field: autoGroupColField,
      sortable: true,
      pinned: "left",
      lockPinned: true,
      filter: "agSetColumnFilter",
      filterParams: {
        buttons: ["reset"],
        defaultToNothingSelected: true,
        values: (params) => {
          const values = masterDataFilterGroups?.current[DATA_MODEL_TABLE.equipmentModel?.key] || [];
          params.success(values);
        },
      },
      showDisabledCheckboxes: true,
      cellStyle: (params) => {
        if (params?.data?.id && getFieldValue(params?.data, "id") === "dummyRow") {
          return { display: "none" };
        }
        if (isEditMode && isOngoing && onGoingEventArray.includes(getFieldValue(params?.data, "id"))) {
          return { "pointer-events": "none" };
        }
        return params?.node?.level > 0 &&
          (getFieldValue(params?.data, "entry_type") === entryType?.cluster ||
            getFieldValue(params?.data, "entry_type") === entryType?.clusterSubequipment)
          ? {
              paddingLeft: 20,
              border: "none",
              backgroundColor: "#F5F5F2",
              backgroundImage: "conic-gradient(at 7% 90%, #FFFFFF 180deg, #F5F5F2 180deg)",
            }
          : {};
      },
      cellRendererParams: {
        suppressCount: false,
        checkbox: (params) => {
          const canSelect = canSelectEquipmentRow({
            data: params?.data,
            nodeLevel: params?.node?.level,
            storeUser,
          });
          params.node.selectable = canSelect;
          return canSelect;
        },

        innerRenderer: (params) => {
          return getFieldValue(params?.data, "equipment_model");
        },
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedList, storeUser, checkUserBookingAccess]);

  const onSave = async ({
    currentState,
    chipName = "",
    type = "update",
    isAgGridObj = false,
    pannel = "",
    isSaveConfigUpdated = false,
    updateRedux = true,
  }) => {
    let result = null;
    if (pannel === TOOL_PANELS.filters) {
      let obj = {};
      if (isAgGridObj) {
        obj = buildAgGridFilterObj(currentState);
      } else {
        obj = {
          ...currentState,
        };
      }

      const lastSavedFilter = {
        filterDefinition: obj,
        filterName: chipName,
      };
      if (type === "create") {
        lastSavedFilter.userID = storeUser?.id;
        lastSavedFilter.chipName = chipName;
        result = await onCreatefilter({
          id: storeUser?.id,
          email: storeUser?.email,
          name: storeUser?.name,
          site: storeUser?.site,
          userFilters: localUserFilters.current,
          lastFavFilter: lastSavedFilter,
          client,
          query: CREATE_USER_FILTER,
          userProfileQuery: UPDATE_DIGITAL_LAB_BOOKING_USER_PROFILE_PERSONAL_FIELDS,
          updateUserFilters: setUserFilters,
          updateLastFavFilter: setLastFavFilter,
        });
      } else {
        let id = storeUser?.id;
        const isBookingAdmin = storeUser.roles?.some((role) => "BookingAdmin");
        let isAdminModifyingOtherUserFilter = false;
        if (isBookingAdmin) {
          const filterToEdit = allAvailableFilters.find((filter) => filter.filterName === chipName);
          if (filterToEdit && filterToEdit.userID !== storeUser?.id) {
            isAdminModifyingOtherUserFilter = true;
          }
          id = filterToEdit?.userID || storeUser?.id;
        }

        result = await onUpdateFilter({
          lastFavFilter: lastSavedFilter,
          isSaveConfigUpdated: isSaveConfigUpdated,
          updateRedux,
          id,
          email: storeUser?.email,
          client,
          query: UPDATE_USER_FILTER,
          userProfileQuery: UPDATE_DIGITAL_LAB_BOOKING_USER_PROFILE_PERSONAL_FIELDS,
          updateLastFavFilter: setLastFavFilter,
          isAdminModifyingOtherUserFilter,
          modifiedBy: storeUser?.name || storeUser?.email || storeUser?.id,
        });
      }
      return { savedObj: lastSavedFilter, result };
    } else if (pannel === TOOL_PANELS.columns) {
      const savedColumns = [...currentState];
      const { showColumns, hideColumns } = buildDisplayDefinitionFromColumns(savedColumns);
      let savecolumnObj = {
        displayName: chipName,
        displayDefinition: {
          showColumns,
          hideColumns,
        },
      };
      if (type === "create") {
        savecolumnObj.userID = storeUser?.id;
        savecolumnObj.chipName = chipName;
        result = await onCreateSelectColumn({
          id: storeUser?.id,
          email: storeUser?.email,
          name: storeUser?.name,
          site: storeUser?.site,
          userDisplayList: userDisplayColumn,
          unParsedData: savecolumnObj,
          client,
          query: CREATE_USER_DISPLAY_COLUMN,
          userProfileQuery: UPDATE_DIGITAL_LAB_BOOKING_USER_PROFILE_PERSONAL_FIELDS,
          updateUserDisplayColumns: setUserDisplayColumn,
          updateLastDisplayColumn: setLastDisplayColumns,
        });
      } else {
        result = await onUpdateSelectColumn({
          data: JSON.stringify(savecolumnObj),
          isSaveConfigUpdated,
          updateLastDisplayColumnWhenEmptyData: false,
          updateRedux,
          id: storeUser?.id,
          email: storeUser?.email,
          name: storeUser?.name,
          client,
          userProfileQuery: UPDATE_DIGITAL_LAB_BOOKING_USER_PROFILE_PERSONAL_FIELDS,
          query: UPDATE_USER_DISPLAY_COLUMN,
          userDisplayList: userDisplayColumn,
          updateUserDisplayColumns: setUserDisplayColumn,
          updateLastDisplayColumn: setLastDisplayColumns,
          updateLastDisplayColumnDefinition,
        });
      }
      return { savedObj: savecolumnObj, result };
    }
  };

  const onChipDelete = async (chipToDelete, lastSavedChip, panel, userFilters) => {
    return await onDeleteSaveConfigurations({
      userID: storeUser?.id,
      chipToDelete,
      panel,
      lastSavedChip,
      client,
      email: storeUser?.email,
      deleteFilterQuery: DELETE_USER_FILTER,
      deleteColumnQuery: DELETE_USER_DISPLAY_COLUMN,
      userProfileQuery: UPDATE_DIGITAL_LAB_BOOKING_USER_PROFILE_PERSONAL_FIELDS,
      userFilters,
      userDisplayList: userDisplayColumn,
      updateUserFilters: setUserFilters,
      updateUserDisplayColumns: setUserDisplayColumn,
      updateLastFavFilter: setLastFavFilter,
      updateLastDisplayColumn: setLastDisplayColumns,
    });
  };

  const onNavigatePath = () => {
    navigation.push("/");
  };

  const onColumnDragStopped = useCallback(
    async (params) => {
      // Get the current column state after drag and drop
      const columnState = params.columnApi.getColumnState();

      // Convert AG Grid column state to the format expected by onSave
      const savedColumns = [...columnState];
      const { showColumns, hideColumns } = buildDisplayDefinitionFromColumns(savedColumns);

      const savecolumnObj = {
        displayName: lastDisplayColumns?.displayName || "",
        displayDefinition: {
          showColumns,
          hideColumns,
        },
      };

      // Save the new column order to lastDisplayColumns (user profile)
      await onUpdateSelectColumn({
        data: JSON.stringify(savecolumnObj),
        isSaveConfigUpdated: false,
        updateLastDisplayColumnWhenEmptyData: false,
        updateRedux: true,
        id: storeUser?.id,
        email: storeUser?.email,
        name: storeUser?.name,
        client,
        userProfileQuery: UPDATE_DIGITAL_LAB_BOOKING_USER_PROFILE_PERSONAL_FIELDS,
        query: UPDATE_USER_DISPLAY_COLUMN,
        userDisplayList: userDisplayColumn,
        updateUserDisplayColumns: setUserDisplayColumn,
        updateLastDisplayColumn: setLastDisplayColumns,
        updateLastDisplayColumnDefinition,
      });
    },
    [
      storeUser,
      client,
      userDisplayColumn,
      lastDisplayColumns,
      setUserDisplayColumn,
      setLastDisplayColumns,
      updateLastDisplayColumnDefinition,
    ],
  );

  const onResetAll = async () => {
    //setSelectedList([]);
    //setEquipmentDetailList([]);
    await onUpdateFilter({
      lastFavFilter: DEFAULT_FILTER,
      updateRedux: true,
      id: storeUser?.id,
      email: storeUser?.email,
      name: storeUser?.name,
      client,
      query: UPDATE_USER_FILTER,
      userProfileQuery: UPDATE_DIGITAL_LAB_BOOKING_USER_PROFILE_PERSONAL_FIELDS,
      updateLastFavFilter: setLastFavFilter,
      isAdminModifyingOtherUserFilter: false,
      modifiedBy: storeUser?.name || storeUser?.email || storeUser?.id,
    });
    await onUpdateSelectColumn({
      data: JSON.stringify(DEFAULT_COLUMNS()),
      updateRedux: true,
      id: storeUser?.id,
      email: storeUser?.email,
      name: storeUser?.name,
      client,
      userProfileQuery: UPDATE_DIGITAL_LAB_BOOKING_USER_PROFILE_PERSONAL_FIELDS,
      query: UPDATE_USER_DISPLAY_COLUMN,
      userDisplayList: userDisplayColumn,
      updateUserDisplayColumns: setUserDisplayColumn,
      updateLastDisplayColumn: setLastDisplayColumns,
    });
    setSelectedList([]);
    setEquipmentDetailList([]);
  };
  const onlyUnique = (list) => {
    return [...new Set(list)];
  };
  const onRowsPerChange = async (rowPerPage) => {
    await saveLastPageSize({
      lastPageSize: rowPerPage,
      query: UPDATE_DIGITAL_LAB_BOOKING_USER_PROFILE_PERSONAL_FIELDS,
      updateLastPageSize: setLastPageSize,
      id: storeUser?.id,
      email: storeUser?.email,
      client,
    });
  };
  const onCheckBoxSelected = ({ id = "", updatedIds = null, rowData = null }) => {
    const isAllowedRow = rowData ? canSelectEquipmentRow({ data: rowData, nodeLevel: 0, storeUser }) : true;

    if (id) {
      if (!isAllowedRow) {
        return;
      }

      setEquipmentDetailList((prevState) => {
        return [...prevState, convertCloudToGraphQLObj(rowData)];
      });
      setSelectedList((prevState) => {
        return onlyUnique([...prevState, id]);
      });
      if (isStepEnable) {
        setNewEquipmentIds((prevState) => {
          return onlyUnique([...prevState, id]);
        });
      }
    }

    if (updatedIds) {
      // Do not re-filter updatedIds via the grid api here: equipmentListRef points
      // at the CommonAgGrid wrapper (which does not expose `.api`), and the grid is
      // serverSide (forEachNode only sees currently-loaded rows) — filtering would
      // wrongly drop valid ids. The grid's own checkbox/selectable callback already
      // prevents selecting non-selectable rows, so updatedIds are already valid.
      setSelectedList(() => updatedIds);

      if (rowData && isAllowedRow) {
        setEquipmentDetailList((prevState) => {
          return [...prevState, convertCloudToGraphQLObj(rowData)];
        });
      }

      setLimitExceedStatus(false);
    }
  };

  // TODO : commented this code as for future use for instrumentDetail info
  // const openInstrumentDetailInfo = async (params) => {
  //   const isBookingControlled = params.data?.is_booking_controlled
  //     ? params.data?.is_booking_controlled[0] === "true"
  //     : false;
  //   const bookableForUsers = params.data?.bookable_for_users
  //     ? params.data?.bookable_for_users[0].includes(storeUser?.user)
  //     : true;
  //   if (
  //     params?.data?.id[0] &&
  //     params?.node?.level === 0 &&
  //     (isBookingControlled || bookableForUsers)
  //   )
  //     history.push("/instrumentDetailedInfo", {
  //       instrument: { inventoryId: params?.data?.id[0] },
  //     });
  // };
  const updateMyLabEquipment = async (id) => {
    const myLabfilters =
      myLabFiltersList.length > 0
        ? myLabFiltersList?.find((x) => x.filterName === DEFAULT_MYLAB_FILTER.filterName)
        : null;

    if (typeof myLabfilters?.filterDefinition === "string") {
      myLabfilters.filterDefinition = JSON.parse(myLabfilters?.filterDefinition);
    }
    //separate default equipment from initial Userfilter
    let filteredArray = [];
    if (myLabCountInitial !== undefined) {
      filteredArray = myLabCountInitial.filter((x) => !myLabCount.includes(x));
    }
    //user selected equipments
    let tempFilterDefinition = myLabfilters ? myLabfilters?.filterDefinition : DEFAULT_MYLAB_FILTER?.filterDefinition;
    tempFilterDefinition.inventoryIds = tempFilterDefinition.inventoryIds
      ? onlyUnique([...tempFilterDefinition.inventoryIds, id])
      : [id];

    //push default to user selected equipments
    let filtertested = tempFilterDefinition?.inventoryIds || [];
    filtertested = filtertested.concat(filteredArray);
    tempFilterDefinition.inventoryIds = onlyUnique(filtertested);

    //DEFAULT_FILTER
    try {
      const shouldUpdate = myLabfilters || hasMyLabFilter.current;

      const saveResult = await landingPageAppSyncClient.mutate({
        mutation: shouldUpdate
          ? UPDATE_DIGITAL_LAB_LANDING_PAGE_USER_FILTER
          : CREATE_DIGITAL_LAB_LANDING_PAGE_USER_FILTER,
        variables: {
          userID: userID,
          filterName: myLabfilters?.filterName ?? DEFAULT_MYLAB_FILTER.filterName,
          filterDefinition: JSON.stringify(tempFilterDefinition),
        },
      });

      if (saveResult?.data?.updateUserFilter || saveResult?.data?.createUserFilter) {
        hasMyLabFilter.current = true;

        const updatedFilter = {
          filterName: DEFAULT_MYLAB_FILTER.filterName,
          filterDefinition: tempFilterDefinition,
          userID: userID,
        };
        setMyLabFiltersList([updatedFilter]);

        setMyLabEquipment(tempFilterDefinition.inventoryIds);
        setAppliedFilter(DEFAULT_MYLAB_FILTER);
        snackbarService.show({
          type: "success",
          icon: "circle_confirm",
          appName: null,
          message: `My lab configuration successfully saved`,
          duration: SNACKBAR_DURATION,
        });

        fetchingMyLabEquipment().then((userFilters) => {
          if (userFilters) {
            setMyLabFiltersList([userFilters]);
          }
        });

        // Reload filter data to update my lab filter
        if (reloadFilterData) {
          await reloadFilterData();
        }
      } else {
        snackbarService.show({
          type: "warning",
          icon: "caution",
          appName: null,
          message: `My lab configuration failed to save`,
          duration: SNACKBAR_DURATION,
        });
      }
    } catch (error) {
      snackbarService.show({
        type: "warning",
        icon: "caution",
        appName: null,
        message: `My lab configuration failed to save ${error.message}`,
        duration: SNACKBAR_DURATION,
      });
    }
  };
  const removeMyLabEquipment = async (id) => {
    if (id) {
      const myLabfilters = myLabFiltersList.find((x) => x.filterName === DEFAULT_MYLAB_FILTER.filterName);
      let tempFilterDefinition = myLabfilters?.filterDefinition;
      const inventoryIds = tempFilterDefinition.inventoryIds.filter((x) => x !== id);
      let filteredArray = [];
      if (myLabCountInitial !== undefined) {
        filteredArray = myLabCountInitial.filter((x) => !id.includes(x));
        tempFilterDefinition.inventoryIds = onlyUnique(inventoryIds.concat(filteredArray));
      }
      //DEFAULT_FILTER
      try {
        const response = await landingPageAppSyncClient.mutate({
          mutation: UPDATE_DIGITAL_LAB_LANDING_PAGE_USER_FILTER,
          variables: {
            userID,
            filterName: myLabfilters?.filterName ?? DEFAULT_MYLAB_FILTER.filterName,
            filterDefinition: JSON.stringify(tempFilterDefinition),
          },
        });

        if (response.data === null || response.errors) {
          throw new Error(JSON.stringify(response.errors));
        }
        snackbarService.show({
          type: "success",
          icon: "circle_confirm",
          title: "",
          duration: SNACKBAR_DURATION,
          message: `My lab configuration successfully saved`,
        });
      } catch (error) {
        snackbarService.show({
          type: "warning",
          icon: "alarm",
          message: error.message,
          duration: SNACKBAR_DURATION,
          title: "Error when saving My lab configuration",
        });
      }

      if (inventoryIds.length > 0) {
        setMyLabEquipment([...inventoryIds]);
      } else {
        setMyLabEquipment([]);
      }
      if (myLabCount?.length !== 0) {
        let myLabId = [];
        myLabId = myLabCount.filter((x) => x !== id);
        setMyLabCount(() => myLabId);
      }

      // Reload filter data to update my lab filter
      if (reloadFilterData) {
        await reloadFilterData();
      }
    }
  };

  useEffect(() => {
    if (listMessage !== "" && listMessage !== null) {
      if (listMessage === INVALID_BARCODE_MESSAGE) {
        snackbarService.show({
          type: "error",
          message: listMessage,
          duration: DURATION,
        });
      }
      if (listMessage === CAMERA_TIMEOUT_MESSAGE) {
        snackbarService.show({
          type: "info",
          message: listMessage,
          duration: DURATION,
        });
      }
      setListMessage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listMessage]);

  const onClickHandler = () => {
    setLimitExceedStatus(false);
  };
  const handleOnclose = () => {
    setLimitExceedStatus(false);
    return true;
  };
  const actionRender = (params) => {
    return (
      <>
        <EquipmentAction
          params={params}
          removeMyLabEquipment={removeMyLabEquipment}
          updateMyLabEquipment={updateMyLabEquipment}
        />
      </>
    );
  };

  const normalizedLastFavFilter = useMemo(() => normalizeConfigObject(lastFavFilter), [lastFavFilter]);
  const normalizedLastDisplayColumns = useMemo(() => normalizeConfigObject(lastDisplayColumns), [lastDisplayColumns]);

  useEffect(() => {
    if (InventoryId) {
      return;
    }

    const isDefaultFilter = isSameConfig(normalizedLastFavFilter, DEFAULT_FILTER);
    const isDefaultColumns = isSameConfig(normalizedLastDisplayColumns, DEFAULT_COLUMNS());

    if (!isDefaultFilter || !isDefaultColumns) {
      return;
    }

    if (equipmentListRef.current?.setState) {
      equipmentListRef.current.setState((prevState) => {
        if (prevState?.disableResetAll) {
          return null;
        }
        return { disableResetAll: true };
      });
    }
  }, [InventoryId, normalizedLastFavFilter, normalizedLastDisplayColumns]);

  return (
    <div className={"makeFlexContent"}>
      {Object.keys(attributes)?.length > 0 && (
        <CommonAgGrid
          ref={equipmentListRef}
          addOnSearchComponent={<ScanCodeButton />}
          storeUser={storeUser}
          height={"100%"}
          autoGroupColumnDefObj={autoGroupColumnDef}
          columnData={dataModel}
          masterDataFilters={masterDataFilters}
          userFilters={userFilters}
          myLabFilter={myLabFilter}
          lastFavFilter={InventoryId ? DEFAULT_FILTER : lastFavFilter}
          lastDisplayColumns={lastDisplayColumns ?? DEFAULT_COLUMNS()}
          userDisplays={userDisplayColumn}
          systemDisplays={[]}
          onSave={onSave}
          onChipDelete={onChipDelete}
          defaultFilterObj={DEFAULT_FILTER}
          defaultShowColumns={defaultShowColumns}
          listOfFieldsIgnoredForSearch={listOfFieldsIgnoredForSearch()}
          listOfNumberFilter={listOfNumberFilter()}
          isServerSideGroupObj={isServerSideGroup}
          getServerSideGroupKeyObj={getServerSideGroupKey}
          selectedEquipment={selectedList}
          onResetAll={onResetAll}
          showSaveConfiguration={true}
          limit={lastPageSize ?? DEFAULT_PAGE_SIZE}
          onRowsPerChange={onRowsPerChange}
          requestServer={requestServer}
          siteName={storeUser?.site}
          onRowClicked={(params) => {
            if (isEditMode && isOngoing && onGoingEventArray.includes(getFieldValue(params?.data, "id"))) {
              bookingPopUpDispatchAction({
                type: "onGoingBooking",
                payload: {
                  onApprove: () =>
                    bookingPopUpDispatchAction({
                      type: "reset",
                    }),
                },
              });
            }
          }}
          onCheckBoxChange={onCheckBoxSelected}
          isBookingApplication={true}
          equipmentInventoryId={InventoryId}
          onNavigatePath={onNavigatePath}
          client={landingPageAppSyncClient}
          onFirstDataRendered={onFirstDataRendered}
          onDragStopped={onColumnDragStopped}
          additionalFields={additionalFields}
          paginationLeftChildren={
            <div style={{ display: "flex", alignItems: "center" }}>
              <OwcBadge
                type="active"
                style={{
                  width: "24px",
                  height: "24px",
                  margin: "0 10px",
                }}
              >
                {selectedList?.length}
              </OwcBadge>
              <OwcTypography variant="body2" style={{ marginRight: "10px" }}>
                {selectedList?.length === 1 ? "Equipment" : "Equipments"} added
              </OwcTypography>
            </div>
          }
          {...attributes}
        >
          <span
            name="pagination"
            style={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "space-between" }}
          >
            {paginationActions}
          </span>
        </CommonAgGrid>
      )}

      <ConfirmDialog
        approveText="Ok"
        approveColor="primary"
        approveVariant="contained"
        onApprove={onClickHandler}
        title={
          <>
            <OwcIcon
              name="alarm"
              type="outlined"
              style={{
                color: "var(--one-color-yellow-a-300)",
                marginRight: 15,
              }}
            />
            <OwcTypography variant="button">Component limit exceeded</OwcTypography>
          </>
        }
        content={
          <OwcTypography variant="subtitle1">
            You can select in maximum <strong>50 components.</strong>
          </OwcTypography>
        }
        disableBooking={false}
        fullWidth={false}
        maxWidth="sm"
        open={limitExceedStatus}
        reasonDropdowmLabel={`Review your selected equipment in "What equipment" section.`}
        close={handleOnclose}
      />

      {cidmDialogOpen ? (
        <CidmGroupDialog
          open={cidmDialogOpen}
          onClose={() => setCidmDialogOpen(false)}
          key={cidmGroupData.name}
          groupName={cidmGroupData.name}
          cidmUrl={cidmGroupData.url}
          infoUrl={cidmGroupData.info}
        />
      ) : null}
    </div>
  );
};

export default withApollo(EquipmentMainPage);
